import { memoize } from "lodash";
import { ShopifyInventoryQuantityKey, ShopifySyncKey } from "./keys";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { isShopifyModelKey, webhooksForModelKey } from "./ShopifyModelMetadata";

export const ModelsRequiringCustomEnrichment = [ShopifyInventoryQuantityKey] as const;

/**
 * Determines if a model is webhook-triggered.
 * A model is webhook-triggered if it has webhooks
 * and it doesn't conditionally inherit webhooks from a parent model
 */
export const isWebhookTriggeredModelKey = memoize(
  (modelKey: string, apiVersion: ShopifyApiVersion, frameworkVersionConstraint: string): boolean => {
    const webhooks = webhooksForModelKey(modelKey, apiVersion, frameworkVersionConstraint, false);

    return Object.keys(webhooks).length > 0;
  },
  (modelKey, apiVersion, frameworkVersionConstraint) => `${modelKey}--${apiVersion}--${frameworkVersionConstraint}`
);

/**
 * Determines if a model is conditionally webhook-triggered.
 * A model is conditionally webhook-triggered if it inherits webhooks from a parent model
 * and the parent models webhook payload does not contain the model's data
 */
export const isConditionallyWebhookTriggeredModelKey = memoize(
  (modelKey: string, apiVersion: ShopifyApiVersion, frameworkVersionConstraint: string): boolean => {
    const webhooksWithAvailability = webhooksForModelKey(modelKey, apiVersion, frameworkVersionConstraint, false);
    const webhooksIgnoringAvailability = webhooksForModelKey(modelKey, apiVersion, frameworkVersionConstraint, true);

    return Object.keys(webhooksIgnoringAvailability).length > 0 && Object.keys(webhooksWithAvailability).length === 0;
  },
  (modelKey, apiVersion, frameworkVersionConstraint) => `${modelKey}--${apiVersion}--${frameworkVersionConstraint}`
);

/**
 * Determines if a model is sync-only.
 * A model is sync-only if it has no webhooks
 * and there is no possibility of it inheriting webhooks from a parent model
 */
export const isSyncOnlyModelKey = memoize(
  (modelKey: string, apiVersion: ShopifyApiVersion, frameworkVersionConstraint: string) => {
    if (modelKey === ShopifySyncKey || !isShopifyModelKey(modelKey)) return false;

    const webhooks = webhooksForModelKey(modelKey, apiVersion, frameworkVersionConstraint, true);

    return Object.keys(webhooks).length === 0;
  },
  (modelKey, apiVersion, frameworkVersionConstraint) => `${modelKey}--${apiVersion}--${frameworkVersionConstraint}`
);
